import { InjectionToken } from "@angular/core";
import { RunActions, RunPlanActivity } from "src/app/model/run";

export type RunTaskFilterFn = (task: RunPlanActivity) => boolean;

export const RUN_TASK_FILTER_CONFIG = new InjectionToken<RunTaskFilterConfig>('Run task filter config', {
  factory: () => config
});

export type RunTaskFilterConfig = {
  controlNo    : string,
  customer     : string,
  location     : string,
  startDatetime: string,
  actions      : {
    pickup         : boolean,
    deliver        : boolean,
    release        : boolean,
    collect        : boolean,
    start          : boolean,
    end            : boolean,
    breakdown      : boolean,
    refuel         : boolean,
    trailerRelease : boolean,
    trailerCollect : boolean,
    create         : boolean,
    accept         : boolean,
    reject         : boolean,
    emptyRunStart  : boolean,
    emptyRunEnd    : boolean,
    uploadPaperwork: boolean
  },
  statuses : {
    notDone: boolean,
    done   : boolean
  }
};

const config: RunTaskFilterConfig = {
  controlNo    : '',
  customer     : '',
  location     : '',
  startDatetime: '',
  actions      : {
    pickup         : true,
    deliver        : true,
    release        : true,
    collect        : true,
    start          : true,
    end            : true,
    breakdown      : true,
    refuel         : true,
    trailerRelease : true,
    trailerCollect : true,
    create         : true,
    accept         : true,
    reject         : true,
    emptyRunStart  : true,
    emptyRunEnd    : true,
    uploadPaperwork: true
  },
  statuses : {
    notDone: true,
    done   : true
  }
};

const taskStatusRules = new Map<string, RunTaskFilterFn>([
  ['done'   , (task) => !!task?.actual?.id],
  ['notDone', (task) =>  !task?.actual?.id],
]);

const taskActionRules = new Map<string, RunTaskFilterFn>([
  ['pickup'         , (task) => task?.run_action_id === RunActions.Pickup],
  ['deliver'        , (task) => task?.run_action_id === RunActions.Deliver],
  ['release'        , (task) => task?.run_action_id === RunActions.Release],
  ['collect'        , (task) => task?.run_action_id === RunActions.Collect],
  ['start'          , (task) => task?.run_action_id === RunActions.Start],
  ['end'            , (task) => task?.run_action_id === RunActions.End],
  ['breakdown'      , (task) => task?.run_action_id === RunActions.Breakdown],
  ['refuel'         , (task) => task?.run_action_id === RunActions.Refuel],
  ['trailerRelease' , (task) => task?.run_action_id === RunActions.TrailerRelease],
  ['trailerCollect' , (task) => task?.run_action_id === RunActions.TrailerCollect],
  ['create'         , (task) => task?.run_action_id === RunActions.Create],
  ['accept'         , (task) => task?.run_action_id === RunActions.Accept],
  ['reject'         , (task) => task?.run_action_id === RunActions.Reject],
  ['emptyRunStart'  , (task) => task?.run_action_id === RunActions.EmptyRunStart],
  ['emptyRunEnd'    , (task) => task?.run_action_id === RunActions.EmptyRunEnd],
  ['uploadPaperwork', (task) => task?.run_action_id === RunActions.UploadPaperwork]
]);

const actionPropsToIds = new Map<string, RunActions>([
  ['pickup'         , RunActions.Pickup],
  ['deliver'        , RunActions.Deliver],
  ['release'        , RunActions.Release],
  ['collect'        , RunActions.Collect],
  ['start'          , RunActions.Start],
  ['end'            , RunActions.End],
  ['breakdown'      , RunActions.Breakdown],
  ['refuel'         , RunActions.Refuel],
  ['trailerRelease' , RunActions.TrailerRelease],
  ['trailerCollect' , RunActions.TrailerCollect],
  ['create'         , RunActions.Create],
  ['accept'         , RunActions.Accept],
  ['reject'         , RunActions.Reject],
  ['emptyRunStart'  , RunActions.EmptyRunStart],
  ['emptyRunEnd'    , RunActions.EmptyRunEnd],
  ['uploadPaperwork', RunActions.UploadPaperwork]
]);

function makeStatusFilter(statuses: RunTaskFilterConfig['statuses']) {
  const orConditions: RunTaskFilterFn[] = [];

  Object.entries(statuses).forEach(([key, value]) => {
    const rule = taskStatusRules.get(key);
    rule && !value && orConditions.push((task) => rule(task));
  });

  const statusFilter: RunTaskFilterFn = (task) => orConditions.some((rule) => rule(task));

  return orConditions.length
    ? statusFilter
    : null;
}

function makeActionFilter(actions: RunTaskFilterConfig['actions']) {
  const orConditions: RunTaskFilterFn[] = [];

  Object.entries(actions).forEach(([key, value]) => {
    const rule = taskActionRules.get(key);
    rule && !value && orConditions.push((task) => rule(task));
  });

  const actionFilter: RunTaskFilterFn = (task) => orConditions.some((rule) => rule(task));
  
  return orConditions.length
    ? actionFilter
    : null;
}

function makeFuzzyStringSearchFilter(
  getProp: (task?: RunPlanActivity) => string | undefined,
  control: RunTaskFilterConfig['controlNo']
)
{
  const toSearch = control.trim().toLowerCase();
  const rule: RunTaskFilterFn = (task) => !getProp(task)
    ?.trim()
    .toLowerCase()
    .includes(toSearch);

  return toSearch.length ? rule : null;
}

function makeStartDatetimeFilter(timestamp: RunTaskFilterConfig['startDatetime']) {
  const rule: RunTaskFilterFn = (task) => new Date(task?.start_datetime_utc).getTime() < new Date(timestamp).getTime();

  return !timestamp?.length
    ? null
    : rule;
}

/**
 * Construct the rules to weed out run tasks for filtering.
 * @param config Filter settings.
 * @returns Functions that filter out tasks.
 */
export function makeRunTaskFilter(config: RunTaskFilterConfig) {
  const controlNoFilter = makeFuzzyStringSearchFilter(
    (task) => task?.connote?.control_no,
    config.controlNo
  );

  const customerFilter = makeFuzzyStringSearchFilter(
    (task) => task?.connote?.customer?.company_name,
    config.customer
  );

  const statusFilter = makeStatusFilter(config.statuses);
  const actionFilter = makeActionFilter(config.actions);
  const startFilter  = makeStartDatetimeFilter(config.startDatetime);

  const andConditions: RunTaskFilterFn[] = [];

  [controlNoFilter, customerFilter, statusFilter, actionFilter, startFilter]
    .filter((rules) => !!rules)
    .forEach((rule) => andConditions.push(rule));

  return andConditions;
}

/**
 * Serialize the run task filter configuration to query parameters.
 * @param config Run task filter configuration.
 * @returns Query string object.
 */
export function toFilterParams(config?: RunTaskFilterConfig) {
  const params = new URLSearchParams();

  const controlNo = config?.controlNo?.trim() ?? '';
  const customer  = config?.customer?.trim() ?? '';
  const location  = config?.location?.trim() ?? '';
  const startDate = config?.startDatetime?.trim() ?? '';
  const isDone    = !!config?.statuses?.done;
  const isPending = !!config?.statuses?.notDone;

  const actionsIncluded: RunActions[] = !config?.actions
    ? []
    : Object.entries(config.actions)
      .map(([key, isChecked]) => isChecked
        ? actionPropsToIds.get(key)
        : undefined
      )
      .filter((action) => !!action);

  controlNo.length && params.append('filter[connote.control_no]', controlNo);
  customer.length  && params.append('filter[connote.customer.company_name]', customer);
  location.length  && params.append('filter[location.long_text]', location);
  startDate.length && params.append('filter[later_than]', startDate);

  if(!isDone && !isPending) {
    params.append('filter[is_done]', `${isDone}`);
    params.append('filter[is_pending]', `${isPending}`);
  }
  else if(!isDone || !isPending) {
    isDone    && params.append('filter[is_done]', `${isDone}`);
    isPending && params.append('filter[is_pending]', `${isPending}`);
  }

  params.append('filter[run_action.id]', actionsIncluded.length
    ? actionsIncluded.join()
    : '-1'
  );

  return params;
}
